// Vignette cartographique statique (SVG, sans WebGL ni réseau) : le territoire mis en avant, ses
// voisins en retrait et, s'il y a lieu, le point d'un dossier. Projection Web Mercator dans le
// cadre. Sert dans la séance de décision et partout où une grande carte serait de trop.
import { cheminSvg, elargirEmprise, emprise, numeroMarqueur, projecteur, type Emprise, type Position, type Territoire } from './carte/geo';
import styles from './VignetteCarte.module.css';

export interface ProprietesVignetteCarte {
  territoire: Territoire;
  point?: Position | null;
  numero?: number;
  libelle: string;
  largeur?: number;
  hauteur?: number;
  className?: string;
}

/** Emprise de la vignette : le territoire (ou ses voisins), élargie au point s'il est en dehors. */
export function empriseVignette(t: Territoire, point?: Position | null): Emprise | null {
  const geometries = [t.geometrie, ...(t.voisins ?? []).map((v) => v.geometrie)].filter((g): g is NonNullable<typeof g> => Boolean(g));
  if (geometries.length === 0 && !point) return null;
  let e: Emprise = t.geometrie ? emprise(t.geometrie) : [Infinity, Infinity, -Infinity, -Infinity];
  if (!t.geometrie) for (const g of geometries) e = union(e, emprise(g));
  if (point) e = union(e, [point[0], point[1], point[0], point[1]]);
  return elargirEmprise(e, 0.08);
}

function union(a: Emprise, b: Emprise): Emprise {
  return [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[2], b[2]), Math.max(a[3], b[3])];
}

export function VignetteCarte({ territoire, point = null, numero, libelle, largeur = 240, hauteur = 160, className }: ProprietesVignetteCarte) {
  const e = empriseVignette(territoire, point);
  if (!e) {
    return (
      <p className={styles.vide} role="img" aria-label={libelle}>
        Aucune limite ni position disponible.
      </p>
    );
  }
  const projeter = projecteur(e, largeur, hauteur, 10);
  const p = point ? projeter(point) : null;
  return (
    <svg className={className ? `${styles.vignette} ${className}` : styles.vignette} viewBox={`0 0 ${largeur} ${hauteur}`} width={largeur} height={hauteur} role="img" aria-label={libelle}>
      <rect width={largeur} height={hauteur} className={styles.terre} />
      {(territoire.voisins ?? []).map((v) => (
        <path key={v.id} d={cheminSvg(v.geometrie, projeter)} className={styles.voisin} strokeDasharray={v.indicatif ? '3 3' : '5 4'} />
      ))}
      {territoire.geometrie ? <path d={cheminSvg(territoire.geometrie, projeter)} className={styles.territoire} strokeDasharray={territoire.indicatif ? '4 3' : undefined} /> : null}
      {p ? (
        <g className={styles.point}>
          <circle cx={p[0]} cy={p[1]} r={11} className={styles.halo} />
          <circle cx={p[0]} cy={p[1]} r={8} />
          {numero !== undefined ? (
            <text x={p[0]} y={p[1] + 3} textAnchor="middle">
              {numeroMarqueur(numero)}
            </text>
          ) : null}
        </g>
      ) : null}
    </svg>
  );
}
